// Animations
import scrollToElement from './animations/scrollTo';

// Components
import updateProgressBar from '../components/_progress-indicators';

// Polyfills
import './polyfills/for-each';
import './polyfills/remove';
import './polyfills/CustomEvent';

// Selectors
import forEachElement from './selectors/forEachElement';
import navbarHeight from './selectors/navbarHeight';

// Utilities
import updateTexts from './leadform-i18n';

import addEventListeners from './events/addEventListeners';
import validateForm from './validation/validateForm';
import dispatchCustomEvent from './events/dispatchCustomEvent';
import getActivePanel from '../leadform/models/form/get_active_panel';

/**
 This file contains all the js for navigating the panels using the next and back buttons.
 It also calls the functions that update the progress indicator, and focus the cursor
 in the input fields.
*/

const previousPanels = [];
const ANIMATION_END_EVENTS = 'webkitAnimationEnd oanimationend msAnimationEnd animationend';

/**
 * Focuses cursor in the panel's first visible input after animation is finished
 *
 * @param {element} panel Active panel, as a DOMElement
 */
const focusAfterAnimation = function(panel) {
  if (panel.classList.contains('lead-constructor__form-fields')) {
    addEventListeners(panel, ANIMATION_END_EVENTS, event => {
      if (event.animationName === 'slide-in') {
        focusFirstInput(panel);
      }
    });
  }
};


function focusFirstInput(element) {
  const firstInput = element.querySelector('input:not([type=hidden])');
  if(firstInput) firstInput.focus();
}

/**
 * Navigates form to next/previous panel
 *
 * @param {element} leadformWrapper Outermost wrapper class of leadform, as a DOMElement
 * @param {string} targetPanelId ID of target panel
 * @param {string} [direction = 'forward'] direction of navigation; either 'forward' or `back`
 */

const goToPanel = function(leadformWrapper, targetPanelId,
    direction = 'forward', formValidator = validateForm,
    scrollUpBetweenPanels = false) {
  const form = leadformWrapper.querySelector('form');
  const currentPanel = getActivePanel(form);
  const currentPanelId = currentPanel.dataset.id;
  let targetPanel = form.querySelector(`.lead-constructor__panel[data-id=${targetPanelId}]`);

  // Update animation direction attribute so correct animations are used
  leadformWrapper.setAttribute('data-animation-direction', direction);

  if (direction === 'back') {
    // Remove active class, but don't mark as complete
    currentPanel.classList.remove('active');

    // Remove from previousPanels array
    previousPanels.pop(currentPanelId);

    // Remove completed class so content will actually show
    targetPanel.classList.remove('complete');
    targetPanel.classList.add('active');

  } else if( formValidator(form) ) { // Only progress forward if current step is valid

    // Trigger event to allow any listener to modify our navigation
    const ev = dispatchCustomEvent(form, 'mslf.beforeNavigation',
      { form, currentPanel, targetPanel });

    // Allow handlers to cancel event or to change targetPanel
    if(ev.defaultPrevented) return;
    targetPanel = ev.detail.targetPanel;

    // Remove active class on current panel and mark it as completed
    currentPanel.classList.remove('active');
    currentPanel.classList.add('complete');

    // Add to previousPanels array so we can navigate back to it
    previousPanels.push(currentPanelId);

    // Add active class to target panel
    targetPanel.classList.add('active');

    // Update any i18n texts within the panel
    setTimeout(function() {
      updateTexts(targetPanel);
    }, 1);

    // Emit an event for consuming apps so they can do their unique things
    // format: 'mslf.name-panel.complete'
    const event = document.createEvent('Event');
    event.initEvent(`mslf.${currentPanelId}.complete`, true, true);
    leadformWrapper.dispatchEvent(event);
  }

  focusAfterAnimation(targetPanel);
  updateProgressBar(leadformWrapper, previousPanels);
  if (scrollUpBetweenPanels) { scrollToElement(leadformWrapper, -navbarHeight()); }

  dispatchCustomEvent(form, 'mslf.afterNavigation', { form, targetPanel });
};

/**
 * Initialize previousPanels array and add listener events on next/back buttons
 *
 * @param {string} [leadformWrapperSelector = '.js-leadform'] Outermost wrapper class of leadform
 */
export const setUpFormNavigation = function(leadformWrapperSelector = '.js-leadform',
  formValidator = validateForm, scrollUpBetweenPanels=false) {
  // Cycle through each leadform separately to handle multiple leadforms per page
  // (otherwise progressing 1 leadform progresses them all)
  forEachElement(leadformWrapperSelector, leadformWrapper => {
    const form = leadformWrapper.querySelector('form');
    const nextButtons = form.querySelectorAll('.js-lead-constructor__next');
    const backButtons = form.querySelectorAll('.js-lead-constructor__back');
    const startingPanel = getActivePanel(form);
    const initialCompletedPanels = form.querySelectorAll('.lead-constructor__panel.complete');
    const getStartedHero = form.querySelector('.js-get-started-hero');
    const getStartedButton = form.querySelector(
      '.lead-constructor__panel--welcome.active .js-lead-constructor__next'
    );

    const initialize = function() {
      // Get any already completed panels
      initialCompletedPanels.forEach((panel) => previousPanels.push(panel.dataset.id));
      // Add first panel to history array and update progress indicator
      previousPanels.push(startingPanel.dataset.id);
      updateProgressBar(leadformWrapper, previousPanels);
    };

    // cancel default fallback actions
    forEachElement(nextButtons, el => el.onclick = null);

    // Bind events
    addEventListeners(nextButtons, 'click', function() {
      const currentPanel = getActivePanel(form);
      if(this.dataset.target != currentPanel.dataset.id){
        leadformWrapper.classList.add('interacted');
        goToPanel(leadformWrapper, this.dataset.target, 'forward',
          formValidator, scrollUpBetweenPanels);
      }
    });

    addEventListeners(backButtons, 'click', function() {
      const currentPanel = getActivePanel(form);
      const prevPanel = previousPanels[previousPanels.length - 1];

      if (prevPanel === 'welcome' || prevPanel === 'welcome-alternate') {
        leadformWrapper.classList.remove('interacted');
      }

      // make sure prevPanel exists and isn't equal to the current panel
      if (prevPanel && prevPanel !== currentPanel.dataset.id) {
        goToPanel(leadformWrapper, prevPanel, 'back',
          formValidator, scrollUpBetweenPanels);
      }
    });

    addEventListeners(getStartedHero, 'click', function() {
      getStartedButton.click();
    });

    initialize();
  });
};

export const setupMultiSelectPanelRedirect = function() {
  // Intercept navigation events and change the target depending on product type
  document.addEventListener('mslf.beforeNavigation', (event) => {
    const { currentPanel, form } = event.detail;

    if (currentPanel.dataset.id == 'product-type') {
      const productTypes = [...form.querySelectorAll('[name="product-type"]')];
      const productTypeIds = productTypes.map(el => el.dataset.product_id);
      const checkedProductTypes = productTypes.filter(el => el.checked);
      const checkedProductTypeIds = checkedProductTypes.map(el => el.dataset.product_id);

      const targetPanel = form.querySelector(
        '.lead-constructor__panel[data-id="product-type-redirect"]'
      );

      // If 'private_tutoring' is not checked
      if(!checkedProductTypeIds.includes('private_tutoring') &&
          checkedProductTypeIds.length >= 1 &&
          targetPanel) {
        event.detail.targetPanel = targetPanel;

        // Hide unchecked items on 'product-type-redirect' panel
        const hiddenTypes = productTypeIds.filter(
          el => !checkedProductTypeIds.includes(el)
        );
        const productTypeRedirectPanelAnchors = targetPanel.querySelectorAll('a');
        const hiddenClassName = 'lead-constructor__button--hidden';

        forEachElement(productTypeRedirectPanelAnchors, el => {
          // If anchor tag has a product_id included in hiddenTypes, add hidden class
          if (hiddenTypes.includes(el.dataset.product_id)) {
            !el.classList.contains(hiddenClassName) && el.classList.add(hiddenClassName);
          } else {
            el.classList.contains(hiddenClassName) && el.classList.remove(hiddenClassName);
          }
        });
      }
    }
  });
};
